/* eslint-disable react/style-prop-object */

import React, {
  type ComponentProps,
  memo,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import * as Clipboard from 'expo-clipboard';
import { ImpactFeedbackStyle } from 'expo-haptics';
import { type Href, useRouter } from 'expo-router';
import {
  defineMessages,
  FormattedMessage,
  FormattedNumber,
  useIntl,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';
import { Platform, StyleSheet, useWindowDimensions, View, type ViewInstance } from 'react-native';

import { ProductCardV2 } from '@garnish/commerce';
import { theme } from '@garnish/constants';
import { BodyText, useHovered } from '@garnish/core';
import { triggerHapticFeedback } from '@garnish/haptics';
import { useNoticeBannersStackActions } from '@garnish/overlays';
import { type Asset } from 'contentful';

import { useProductMicroNutritionData } from '@sg/shared-fe-features-menu';
import { ProductDetails } from '@sg/shared-fe-features-product-details';

import { MenuCategoriesGrid } from '@order/components-menu';
import { ProductDetailsCardCarousel } from '@order/features-product-details';

import { type useOneClickAddToBag } from '../../../../hooks/useOneClickAddToBag';
import { type MenuContentProduct } from '../../MenuContent.types';
import { MenuProductCardLineageOverlay } from './components';
import { useProductDetailsCarouselItems } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentProductCard = memo((props: MenuContentProductCardProps) => {
  const {
    product,
    variant = 'standard',
    shouldHideMacros = false,
    imageFetchingPriority,
    hoverImageUrl,
    hoverStaticImageUrl,
    hoverImageAlt,
    carousel,
    hasProductDetails,
    generateHref,
    calorieOverride,
    calorieOverrideText,
    isLineageKeyCopyModeEnabled = false,
    restrictionNotice,
    addProductToBag,
    isAddingProductToBag,
    onCardPress,
    onProductDetailsLinkPress,
  } = props;

  const { baseProduct, isModifiable, name, slug } = product;

  const router = useRouter();
  const { formatMessage } = useIntl();
  const { clear: clearNoticeBanners, push: addNoticeBanner } = useNoticeBannersStackActions();

  // ─── Refs ────────────────────────────────────────────────────────────

  const cardContainerRef = useRef<ViewInstance>(null);
  const isHovered = useHovered(cardContainerRef);

  // ─── Derived Data ────────────────────────────────────────────────────

  const href = useMemo(() => generateHref(slug), [slug, generateHref]);

  const productDetailsHref = baseProduct?.slug ? `/product-details/${baseProduct.slug}` : '';

  const isLineageKeyAvailable = Boolean(baseProduct.lineageKey);
  const shouldShowLineageKeyCopyHover = isLineageKeyCopyModeEnabled && isHovered;

  // ─── Nutrition Data ──────────────────────────────────────────────────

  const baseProductWithNutritionData = useMemo(() => {
    if (typeof calorieOverride === 'number') {
      return { ...baseProduct, calories: calorieOverride };
    }

    return baseProduct;
  }, [baseProduct, calorieOverride]);

  const macroNutritionData = useProductMicroNutritionData({
    baseProduct: baseProductWithNutritionData,
  });
  const { calories } = macroNutritionData.record;

  const caloriesNutritionData = calories ? [calories] : [];
  const nutritionData = shouldHideMacros ? caloriesNutritionData : macroNutritionData.entries;

  // ─── Flags ───────────────────────────────────────────────────────────

  const isProductAvailable = !product.outOfStock;
  const isProductSupportsOneClickAddToBag = !isModifiable;
  const isNavigationEnabled = isProductAvailable && !isProductSupportsOneClickAddToBag;

  /**
   * Customers can add products to their bags with a single click by pressing
   * the entire product card or the standard 1-click CTA button.
   */
  const shouldUsePressableVariation = isProductAvailable && isProductSupportsOneClickAddToBag;

  const shouldRenderProductDetailsLink = hasProductDetails && baseProduct?.slug;
  const shouldRenderStandardAddToBagIcon = variant === 'standard' && isProductSupportsOneClickAddToBag; // prettier-ignore

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOneClickAddToBag = useCallback(async () => {
    await addProductToBag(product, 1);
  }, [addProductToBag, product]);

  const trackCardPress = useCallback(() => {
    onCardPress(product);
  }, [onCardPress, product]);

  const trackProductDetailsLinkPress = useCallback(() => {
    onProductDetailsLinkPress(product);
  }, [onProductDetailsLinkPress, product]);

  const handleCarouselSlidePress = useCallback(() => {
    if (shouldUsePressableVariation) {
      void handleOneClickAddToBag();

      return;
    }

    if (isNavigationEnabled) {
      void triggerHapticFeedback(ImpactFeedbackStyle.Light);
      router.navigate(href);
      trackCardPress();
    }
  }, [
    shouldUsePressableVariation,
    handleOneClickAddToBag,
    isNavigationEnabled,
    href,
    router,
    trackCardPress,
  ]);

  const handleCopyLineageKey = useCallback(async () => {
    const { lineageKey } = baseProduct;

    if (!lineageKey) return;

    const hasCopied = await Clipboard.setStringAsync(lineageKey);

    if (!hasCopied) return;

    clearNoticeBanners();
    addNoticeBanner({
      id: 'menu-lineage-key-copy-success',
      palette: 'success',
      text: formatMessage(messages.lineageKeyCopied, { product_name: name }),
      highlights: [name],
      autoHideTimeout: 2000,
    });
  }, [addNoticeBanner, baseProduct, clearNoticeBanners, formatMessage, name]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View ref={cardContainerRef} style={styles.cardContainer}>
      {shouldRenderProductDetailsLink ? (
        <ProductDetails.Link
          href={productDetailsHref}
          name={product.name}
          slug={baseProduct.slug}
          onPress={trackProductDetailsLinkPress}
        />
      ) : null}

      <MenuContentProductCardContainer
        href={href}
        onPress={trackCardPress}
        onQuickAddToBag={handleOneClickAddToBag}
        isLoading={isAddingProductToBag}
        isNavigationEnabled={isNavigationEnabled}
        productName={name}
        variation={shouldUsePressableVariation ? 'pressable' : 'link'}
      >
        {variant === 'dense' ? (
          <MenuContentProductCardDenseContent
            product={product}
            nutritionData={nutritionData}
            imageFetchingPriority={imageFetchingPriority}
            calorieOverrideText={calorieOverrideText}
            restrictionNotice={restrictionNotice}
            isProductAvailable={isProductAvailable}
            isProductSupportsOneClickAddToBag={isProductSupportsOneClickAddToBag}
            isAddingProductToBag={isAddingProductToBag}
          />
        ) : (
          <MenuContentProductCardStandardContent
            product={product}
            nutritionData={nutritionData}
            imageFetchingPriority={imageFetchingPriority}
            hoverImageUrl={hoverImageUrl}
            hoverStaticImageUrl={hoverStaticImageUrl}
            hoverImageAlt={hoverImageAlt}
            carousel={carousel}
            calorieOverrideText={calorieOverrideText}
            restrictionNotice={restrictionNotice}
            isProductAvailable={isProductAvailable}
            canHover={isNavigationEnabled || shouldUsePressableVariation}
            onCarouselSlidePress={handleCarouselSlidePress}
          />
        )}
      </MenuContentProductCardContainer>

      {shouldRenderStandardAddToBagIcon ? (
        <ProductCardV2.AddToBagContainer>
          <ProductCardV2.AddToBagIcon
            isLoading={isAddingProductToBag}
            isDisabled={!isProductAvailable}
          />
        </ProductCardV2.AddToBagContainer>
      ) : null}

      {shouldShowLineageKeyCopyHover ? (
        <MenuProductCardLineageOverlay
          isLineageKeyAvailable={isLineageKeyAvailable}
          productName={product.name}
          onPress={handleCopyLineageKey}
        />
      ) : null}
    </View>
  );
});

// ─── Standard Content ────────────────────────────────────────────────────────

const MenuContentProductCardStandardContent = (props: StandardContentProps) => {
  const {
    product,
    nutritionData,
    imageFetchingPriority,
    hoverImageUrl,
    hoverStaticImageUrl,
    hoverImageAlt,
    carousel,
    calorieOverrideText,
    restrictionNotice,
    isProductAvailable,
    canHover,
    onCarouselSlidePress,
  } = props;

  const { name, description, asset, label, cost } = product;

  const { fontScale } = useWindowDimensions();

  const { carouselItems } = useProductDetailsCarouselItems({
    carousel,
    productAssetUrl: asset.url,
    productName: name,
  });

  const nutritionDataEntriesLineBreak = fontScale >= 1.3 ? 2 : undefined;

  // ─── Flags ───────────────────────────────────────────────────────────

  const shouldRenderNutritionData = nutritionData.length > 0;

  // NOTE: On the web platform, we don't show the carousel in favor of hover image
  const shouldRenderCarousel = Platform.OS !== 'web' && carouselItems.length > 0;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ProductCardV2.Container canHover={canHover} style={styles.container}>
      {!shouldRenderCarousel && hoverImageUrl ? (
        <ProductCardV2.HoverImage
          source={hoverImageUrl}
          staticSource={hoverStaticImageUrl}
          alt={hoverImageAlt}
        />
      ) : null}

      {!shouldRenderCarousel && asset.url ? (
        <ProductCardV2.Image
          source={asset.url}
          alt={name}
          isUnavailable={!isProductAvailable}
          priority={imageFetchingPriority}
        />
      ) : null}

      {shouldRenderCarousel ? (
        <ProductDetailsCardCarousel
          items={carouselItems}
          onSlidePress={isProductAvailable ? onCarouselSlidePress : undefined}
        />
      ) : null}

      <MenuContentProductCardLabels label={label?.name} isProductAvailable={isProductAvailable} />

      <ProductCardV2.Heading>{name}</ProductCardV2.Heading>
      <ProductCardV2.Description>{description}</ProductCardV2.Description>

      {shouldRenderNutritionData ? (
        <ProductCardV2.NutritionData.Stack lineBreakAfter={nutritionDataEntriesLineBreak}>
          {nutritionData.map((entry) => (
            <ProductCardV2.NutritionData.Item
              key={entry.label}
              value={entry.value}
              label={entry?.labelShort ?? entry.label}
            />
          ))}
        </ProductCardV2.NutritionData.Stack>
      ) : null}

      {calorieOverrideText ? (
        <ProductCardV2.Notice style={styles.notice}>{calorieOverrideText}</ProductCardV2.Notice>
      ) : null}

      <ProductCardV2.Footer>
        <ProductCardV2.ProductDetailsContainer>
          <ProductCardV2.ProductDetail palette="dark-kale">
            <FormattedNumber value={cost / 100} style="currency" currency="USD" />
          </ProductCardV2.ProductDetail>
        </ProductCardV2.ProductDetailsContainer>

        {restrictionNotice ? (
          <ProductCardV2.AllergenNotice>{restrictionNotice}</ProductCardV2.AllergenNotice>
        ) : null}
      </ProductCardV2.Footer>
    </ProductCardV2.Container>
  );
};

// ─── Dense Content ───────────────────────────────────────────────────────────

const MenuContentProductCardDenseContent = (props: DenseContentProps) => {
  const {
    product,
    nutritionData,
    imageFetchingPriority,
    calorieOverrideText,
    restrictionNotice,
    isProductAvailable,
    isProductSupportsOneClickAddToBag,
    isAddingProductToBag,
  } = props;

  const { name, description, asset, label, cost } = product;

  return (
    <View style={styles.denseContent}>
      <View style={styles.denseImageContainer}>
        {asset.url ? (
          <ProductCardV2.Image
            source={asset.url}
            alt={name}
            isUnavailable={!isProductAvailable}
            priority={imageFetchingPriority}
            size="small"
          />
        ) : null}

        {isProductSupportsOneClickAddToBag ? (
          <ProductCardV2.AddToBagContainer style={styles.denseAddToBagContainer}>
            <ProductCardV2.AddToBagIcon
              isLoading={isAddingProductToBag}
              isDisabled={!isProductAvailable}
              style={styles.denseAddToBagIcon}
            />
          </ProductCardV2.AddToBagContainer>
        ) : null}
      </View>

      <MenuContentProductCardLabels label={label?.name} isProductAvailable={isProductAvailable} />

      <ProductCardV2.Heading sizeMatch={['16']}>{name}</ProductCardV2.Heading>

      {description ? (
        <BodyText sizeMatch={['12']} style={styles.denseDescription}>
          {description}
        </BodyText>
      ) : null}

      {restrictionNotice ? (
        <ProductCardV2.AllergenNotice>{restrictionNotice}</ProductCardV2.AllergenNotice>
      ) : null}

      <View style={styles.denseFooter}>
        {nutritionData.length > 0 ? (
          <ProductCardV2.NutritionData.Stack>
            {nutritionData.map((entry) => (
              <ProductCardV2.NutritionData.Item
                key={entry.label}
                value={entry.value}
                label={entry.labelShort ?? entry.label}
              />
            ))}
          </ProductCardV2.NutritionData.Stack>
        ) : null}

        {calorieOverrideText ? (
          <ProductCardV2.Notice>{calorieOverrideText}</ProductCardV2.Notice>
        ) : null}

        <ProductCardV2.ProductDetail sizeMatch={['12']}>
          <FormattedNumber value={cost / 100} style="currency" currency="USD" />
        </ProductCardV2.ProductDetail>
      </View>
    </View>
  );
};

// ─── Labels ──────────────────────────────────────────────────────────────────

const MenuContentProductCardLabels = (props: LabelsProps) => {
  const { label, isProductAvailable } = props;

  return (
    <ProductCardV2.LabelContainer>
      {isProductAvailable && label ? <ProductCardV2.Label>{label}</ProductCardV2.Label> : null}

      {isProductAvailable ? null : (
        <ProductCardV2.UnavailableLabel>
          <FormattedMessage {...messages.unavailableProductLabel} />
        </ProductCardV2.UnavailableLabel>
      )}
    </ProductCardV2.LabelContainer>
  );
};

// ─── Container ───────────────────────────────────────────────────────────────

const MenuContentProductCardContainer = (props: MenuContentProductCardContainerProps) => {
  const {
    variation,
    href,
    productName,
    isNavigationEnabled,
    isLoading,
    children,
    onPress,
    onQuickAddToBag,
  } = props;

  if (variation === 'pressable') {
    return (
      <MenuCategoriesGrid.ProductCardPressable
        onPress={onQuickAddToBag}
        isDisabled={isLoading}
        isLoading={isLoading}
        productName={productName}
      >
        {children}
      </MenuCategoriesGrid.ProductCardPressable>
    );
  }

  return (
    <MenuCategoriesGrid.ProductCardLink
      href={href}
      onPress={onPress}
      isNavigationEnabled={isNavigationEnabled}
      productName={productName}
    >
      {children}
    </MenuCategoriesGrid.ProductCardLink>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly unavailableProductLabel: NoMessageValues;
  readonly quickAddToBagA11yLabel: { readonly product: MessageValue };
  readonly lineageKeyCopied: { readonly product_name: MessageValue };
}>({
  unavailableProductLabel: {
    defaultMessage: 'Currently unavailable',
    description: 'Menu | Product card | Unavailable product label',
  },
  quickAddToBagA11yLabel: {
    defaultMessage: 'Add 1 {product} to your bag',
    description: 'Menu | Product card | 1-click add to bag | a11y label',
  },
  lineageKeyCopied: {
    defaultMessage: 'Copied lineage key for {product_name}',
    description: 'Menu | Product card | Lineage key copied confirmation',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  notice: {
    marginTop: 6,
  },
  cardContainer: {
    flex: 1,
  },
  denseContent: {
    flex: 1,
    rowGap: theme.spacing['1'],
  },
  denseImageContainer: {
    position: 'relative',
    marginBottom: theme.spacing['1'],
  },
  denseAddToBagContainer: {
    right: theme.spacing['2'],
    bottom: theme.spacing['2'],
  },
  denseAddToBagIcon: {
    backgroundColor: theme.colors.WHITE,
  },
  denseDescription: {
    color: theme.colors.CHARCOAL,
  },
  denseFooter: {
    marginTop: 'auto',
    paddingTop: theme.spacing['2'],
    rowGap: theme.spacing['1'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ImageFetchingPriority = ComponentProps<typeof ProductCardV2.Image>['priority'];
type Carousel = { fields: { imageVideo: Asset; staticImage?: Asset } }[] | undefined;
type NutritionData = ReturnType<typeof useProductMicroNutritionData>['entries'];

type MenuContentProductCardProps = {
  product: MenuContentProduct;
  variant?: 'standard' | 'dense';
  shouldHideMacros?: boolean;
  imageFetchingPriority: ImageFetchingPriority;
  hoverImageUrl: string | undefined;
  hoverStaticImageUrl: string | undefined;
  hoverImageAlt: string | undefined;
  carousel: Carousel;
  generateHref: (productSlug: string) => Href;
  hasProductDetails: boolean;
  calorieOverride?: number;
  calorieOverrideText?: string;
  isLineageKeyCopyModeEnabled?: boolean;
  restrictionNotice: string | undefined;
  addProductToBag: ReturnType<typeof useOneClickAddToBag>['addProductToBag'];
  isAddingProductToBag: boolean;
  onCardPress: (product: MenuContentProduct) => void;
  onProductDetailsLinkPress: (product: MenuContentProduct) => void;
};

type CardContentBaseProps = {
  product: MenuContentProduct;
  nutritionData: NutritionData;
  imageFetchingPriority: ImageFetchingPriority;
  calorieOverrideText: string | undefined;
  restrictionNotice: string | undefined;
  isProductAvailable: boolean;
};

type StandardContentProps = CardContentBaseProps & {
  hoverImageUrl: string | undefined;
  hoverStaticImageUrl: string | undefined;
  hoverImageAlt: string | undefined;
  carousel: Carousel | undefined;
  canHover: boolean;
  onCarouselSlidePress: () => void;
};

type DenseContentProps = CardContentBaseProps & {
  isProductSupportsOneClickAddToBag: boolean;
  isAddingProductToBag: boolean;
};

type LabelsProps = {
  label: string | undefined;
  isProductAvailable: boolean;
};

type MenuContentProductCardContainerProps = PropsWithChildren<{
  variation: 'link' | 'pressable';
  href: Href;
  onQuickAddToBag: () => void;
  onPress?: () => void;
  isLoading: boolean;
  isNavigationEnabled: boolean;
  productName: string;
}>;
